import { useMemo, useRef, useState } from "react";
import { ColDef, ColumnState } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import { GridContextWrapper } from "src/components/common/gridWrapper/GridContextWrapper";
import Grid_Select from "src/components/common/gridWrapper/Grid_Select";
import { PDA1060_R01Dto } from "src/containers/PDA/PDA1060_R01/PDA1060_R01Dto";
import {
  aggridHeaderClass,
  pdaCellStyle,
} from "src/components/common/common";
import usePDA1060_R01Store from "./usePDA1060_R01Store";

type PDA1060_R01DtoWithInserted = PDA1060_R01Dto & { inserted?: boolean };

/** PDA1060_R01 재고 조회 테이블 - 창고 위치, LOT, 수량 (PDA1090_R01 스타일) */
const PDA1060_R01Table = () => {
  const { stockList } = usePDA1060_R01Store();
  const gridRef = useRef<AgGridReact<PDA1060_R01DtoWithInserted>>(null);
  const gridWrapperRef = useRef<HTMLDivElement>(null);
  const [hoveredRowIndex, setHoveredRowIndex] = useState<number | null>(null);
  const [columnState, setColumnState] = useState<ColumnState[] | null>(null);

  const totalStockQty = stockList.reduce((sum, item) => sum + (item.stockQty || 0), 0);

  const paddedGridData = useMemo(
    () => [...stockList] as PDA1060_R01DtoWithInserted[],
    [stockList]
  );

  const pinnedBottomRowData = useMemo(
    () =>
      [
        {
          warehouseNm: "총계",
          locationCd: "",
          lotNo: "",
          stockQty: totalStockQty,
        } as PDA1060_R01DtoWithInserted,
      ],
    [totalStockQty]
  );

  const columnDefs: ColDef<PDA1060_R01DtoWithInserted>[] = useMemo(() => {
    const cellStyleLeft: Record<string, string> = {
      textAlign: "left",
      ...pdaCellStyle,
      justifyContent: "flex-start",
    };
    const cellStyleRight: Record<string, string> = {
      textAlign: "right",
      ...pdaCellStyle,
      justifyContent: "flex-end",
    };
    return [
      {
        headerName: "창고",
        field: "warehouseNm",
        valueGetter: (params) => {
          if (params.node?.rowPinned === "bottom") return params.data?.warehouseNm ?? "총계";
          return (
            params.data?.warehouseNm ??
            params.data?.resultWarehouseCd ??
            params.data?.warehouseCd ??
            "-"
          );
        },
        cellStyle: cellStyleLeft,
        headerClass: aggridHeaderClass,
        flex: 2,
        minWidth: 90,
      },
      {
        headerName: "위치",
        field: "locationCd",
        valueGetter: (params) => {
          if (params.node?.rowPinned === "bottom") return "";
          const loc = params.data?.locationNm || params.data?.locationCd;
          return loc && loc.trim() !== "" ? loc : "-";
        },
        cellStyle: cellStyleLeft,
        headerClass: aggridHeaderClass,
        flex: 2,
        minWidth: 90,
      },
      {
        headerName: "LOT",
        field: "lotNo",
        valueGetter: (params) => {
          if (params.node?.rowPinned === "bottom") return "";
          return params.data?.lotNo ?? "-";
        },
        cellStyle: cellStyleLeft,
        headerClass: aggridHeaderClass,
        flex: 2,
        minWidth: 90,
      },
      {
        headerName: "수량",
        field: "stockQty",
        valueGetter: (params) => {
          if (params.node?.rowPinned === "bottom") return params.data?.stockQty;
          return params.data?.stockQty ?? null;
        },
        valueFormatter: (params) => {
          if (params.value === null || params.value === undefined) return "-";
          return params.value.toLocaleString();
        },
        cellStyle: cellStyleRight,
        headerClass: aggridHeaderClass,
        flex: 1.2,
        minWidth: 80,
      },
    ];
  }, []);

  // 선택 초기화 함수 (필수이지만 사용하지 않음)
  const initSelected = () => {};

  return (
    <div className="flex-1 p-3 pt-2 flex w-full h-full flex-col overflow-hidden" >
      <GridContextWrapper
        gridRef={gridRef}
        gridWrapperRef={gridWrapperRef}
        paddedGridData={paddedGridData}
        initSelected={initSelected}
        baseColumnDefs={columnDefs}
        setColumnState={setColumnState}
        localStorageKey="PDA1060_R01_ColumnState"
        enableDataHistory={false}
      >
        <div className="flex-1 w-full h-full min-h-0 text-base">
          <Grid_Select<PDA1060_R01DtoWithInserted>
            ref={gridRef}
            rowData={paddedGridData}
            columnDefs={columnDefs}
            setHoveredRowIndex={setHoveredRowIndex}
            columnStateKey="PDA1060_R01_ColumnState"
            setColumnState={setColumnState}
            pinnedBottomRowData={pinnedBottomRowData}
          />
        </div>
      </GridContextWrapper>
    </div>
  );
};

export default PDA1060_R01Table;
